import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2, Search } from 'lucide-react';
import { formatarCNPJ, normalizarCNPJ, validarCNPJ } from '@/utils/cnpj';
import { useQueryClient } from '@tanstack/react-query';
import { useDossieCnpj } from '@/hooks/useDossieCnpj';
import { DossieCnpjCard } from '@/components/ecossistema-api/DossieCnpjCard';
import { useEcossistemaApi, type EcossistemaConsultaResult } from '@/hooks/useEcossistemaApi';
import { useToast } from '@/hooks/use-toast';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';

interface LeadDados360ApiProps {
  lead: LeadCarteira;
  onLigar?: (telefone: string) => void;
  campanhaNome?: string | null;
}

/**
 * Visão "Dados" para leads ECOSSISTEMA_API.
 *
 * Reusa o DossieCnpjCard (mesmo componente da aba CNPJ do Ecossistema).
 * Lê a VIEW vw_dossie_cnpj e converte para o shape de EcossistemaConsultaResult
 * que o DossieCnpjCard espera. Assim os 5 cards (Score, Empresa Receita,
 * Empresa enriquecida Assertiva, Cobertura BL por endereço, Sócios decisores
 * com telefones destacados) ficam idênticos à tela de consulta CNPJ.
 */
export function LeadDados360Api({ lead }: LeadDados360ApiProps) {
  const { data: dossie, isLoading } = useDossieCnpj(lead.cnpj);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { consultarAsync, isLoading: isConsultando } = useEcossistemaApi();

  const cnpjLimpo = normalizarCNPJ(lead.cnpj || '');
  const cnpjValido = validarCNPJ(cnpjLimpo);

  const handleConsultarAgora = async () => {
    if (!cnpjValido) {
      toast({ title: 'CNPJ inválido', description: 'Este lead não tem CNPJ válido.', variant: 'destructive' });
      return;
    }
    try {
      await consultarAsync({ tipo: 'CNPJ', valor: cnpjLimpo });
      // Re-busca o dossiê e invalida caches relacionados pra UI atualizar
      queryClient.invalidateQueries({ queryKey: ['dossie-cnpj', cnpjLimpo] });
      toast({ title: 'Ecossistema consultado', description: 'Dossiê atualizado.' });
    } catch (err: any) {
      toast({
        title: 'Falha na consulta',
        description: err?.message || 'Erro ao consultar Ecossistema.',
        variant: 'destructive',
      });
    }
  };

  // Converte o shape da VIEW para o shape EcossistemaConsultaResult que o
  // DossieCnpjCard usa como fonte primária. Inclui:
  //  - DEDUP de sócios por cpf (evita 24 cópias do mesmo sócio se a tabela
  //    estiver com lixo de antes do fix da edge)
  //  - cobertura_por_cep: monta a partir de cep_matriz + tem_cobertura_bl
  //  - telefones_origem: marca todos como 'empresa' (origem da consolidação
  //    em telefones_operadora)
  const orquestradorData = useMemo<EcossistemaConsultaResult | null>(() => {
    if (!dossie) return null;

    // Dedup de sócios por CPF (defensivo — caso a tabela tenha duplicatas
    // antigas que a constraint nova ainda não removeu).
    const sociosMap = new Map<string, any>();
    for (const s of dossie.socios || []) {
      const key = (s.cpf || s.nome || '').replace(/\D/g, '') || s.nome || Math.random().toString();
      if (!sociosMap.has(key)) sociosMap.set(key, s);
    }
    const sociosUnicos = Array.from(sociosMap.values());

    const sociosEnriquecidos = sociosUnicos.map((s) => ({
      socio_original: {
        cpf: s.cpf,
        nomeOuRazaoSocial: s.nome,
        nome: s.nome,
        qualificacao: s.qualificacao,
        participacao: s.participacao,
        dataEntrada: s.data_entrada,
      },
      enriquecimento: s.dados_extra
        ? {
            dadosCadastrais: { nome: s.nome },
            telefones: s.dados_extra.telefones || [],
            emails: s.dados_extra.emails || [],
            enderecos: s.dados_extra.enderecos || [],
            redesSociais: s.dados_extra.redesSociais || [],
            participacoesEmpresas: s.dados_extra.participacoes || [],
          }
        : null,
    }));

    // Telefones consolidados → shape operadoras
    const operadoras = (dossie.telefones || []).map((t) => ({
      'NÚMERO': t.telefone,
      'OPERADORA': t.operadora || '',
      'DATA PORTABILIDADE': (t.historico || [])[0]?.data_portabilidade || '',
      FONTE: 'view_dossie' as any,
    }));

    // telefones_origem: VIEW não diferencia origem — marca todos como 'empresa'.
    // Permite que o DossieCnpjCard agrupe corretamente (origem='empresa' →
    // bloco "Telefones da empresa" no card de cada sócio).
    const telefonesOrigem: Record<string, { origem: string; detalhe?: string }> = {};
    for (const t of dossie.telefones || []) {
      telefonesOrigem[t.telefone] = { origem: 'empresa', detalhe: 'consolidado' };
    }

    // cobertura_por_cep: a VIEW só traz tem_cobertura_bl (boolean) e
    // cep_matriz. Constrói entrada única para a matriz com o status
    // conhecido. Endereços secundários ficam com cobertura unknown.
    const coberturaPorCep: Record<string, any> = {};
    if (dossie.cep_matriz) {
      coberturaPorCep[dossie.cep_matriz] = {
        ativo: dossie.tem_cobertura_bl === true,
        tipo_servico: null,
        velocidade_max_mbps: null,
        contexto: 'matriz',
      };
    }

    // Assertiva: o JSONB salvo em assertiva_consultas.resultado tem shape
    // { cabecalho, resposta: { dadosCadastrais, socios, telefones, ... } }.
    // O DossieCnpjCard espera o conteúdo de `resposta` direto (data.assertiva
    // já desempacotado). Tenta as duas formas para compatibilidade.
    const assertivaRaw = dossie.assertiva_payload?.resultado || null;
    const assertiva = assertivaRaw?.resposta || assertivaRaw || null;

    return {
      ok: true,
      tipo: 'CNPJ',
      cascata_status: {
        receita: 'ok',
        assertiva: assertiva ? 'ok' : 'erro',
        operadora: 'ok',
        cobertura: dossie.tem_cobertura_bl == null ? 'sem_cep' : 'ok',
      } as any,
      cnpj: dossie.cnpj,
      receita: dossie.receita_payload,
      assertiva,
      socios_enriquecidos: sociosEnriquecidos,
      operadoras,
      telefones_origem: telefonesOrigem,
      cobertura_por_cep: coberturaPorCep,
      cep_matriz_consultado: dossie.cep_matriz,
      tem_cobertura_bl: dossie.tem_cobertura_bl,
      score: dossie.score,
      contadores: {
        total_telefones: dossie.total_telefones,
        total_celulares: dossie.total_celulares_nao_claro,
        total_celulares_nao_claro: dossie.total_celulares_nao_claro,
        tem_celular_nao_claro: dossie.total_celulares_nao_claro > 0,
      },
    };
  }, [dossie]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!dossie || !orquestradorData) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground space-y-4">
          <Sparkles className="h-10 w-10 mx-auto opacity-40" />
          <p className="text-sm">
            Dossiê ainda não consultado para <strong>{formatarCNPJ(lead.cnpj || '')}</strong>.
          </p>
          <Button onClick={handleConsultarAgora} disabled={isConsultando || !cnpjValido} className="gap-2">
            {isConsultando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {isConsultando ? 'Consultando Receita, Assertiva, operadoras...' : 'Consultar Ecossistema agora'}
          </Button>
          <p className="text-[11px] text-muted-foreground">
            Cascata: Receita Federal → Assertiva (sócios + contatos) → Operadoras → Cobertura Claro BL.
            Persistido em Consultas Salvas — sem repetir consumo se já foi consultado.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Reusa o DossieCnpjCard com o adapter — visualmente idêntico à aba CNPJ.
  return (
    <DossieCnpjCard
      cnpj={dossie.cnpj}
      orquestradorData={orquestradorData}
      cascataStatus={orquestradorData.cascata_status}
    />
  );
}
